'use client';

import { useState, useTransition } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import {
  type CancellationInput,
  CancellationSchema,
} from '../_lib/cancellation.schema';
import { sendCancellation } from '../_lib/server/server-actions';

/**
 * Kündigungsformular gemäß § 312k BGB (Kündigungsbutton ohne Login) –
 * Prototyp-Markup (.locari-form/.field/.form-hint/.form-note/.form-success)
 * verdrahtet mit react-hook-form + Zod gegen die eigenständige Server-Action.
 *
 * § 312k verlangt einen klar bezeichneten Kündigungsbutton + einen
 * Bestätigungs-Button: „Verträge hier kündigen" → „jetzt kündigen".
 */
export function CancellationForm() {
  const [confirming, setConfirming] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pending, startTransition] = useTransition();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CancellationInput>({
    resolver: zodResolver(CancellationSchema),
    defaultValues: {
      name: '',
      email: '',
      reference: '',
      reason: '',
    },
  });

  const onSubmit = handleSubmit((values) => {
    // Erster gültiger Submit → Bestätigungsschritt anzeigen.
    if (!confirming) {
      setConfirming(true);
      return;
    }

    startTransition(async () => {
      try {
        await sendCancellation(values);
        setSubmitted(true);
      } catch {
        setError('root', {
          message:
            'Senden fehlgeschlagen. Bitte versuche es erneut oder schreib uns direkt.',
        });
      }
    });
  });

  if (submitted) {
    return (
      <div className="form-success is-visible" role="status" aria-live="polite">
        <span className="success-mark" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <h3>Deine Kündigung ist eingegangen.</h3>
        <p>
          Wir haben dir eine Bestätigung mit dem Zeitpunkt des Zugangs per
          E-Mail geschickt und kümmern uns um die Schließung deines Kontos.
        </p>
      </div>
    );
  }

  return (
    <form className="locari-form" onSubmit={onSubmit} noValidate>
      <div className="field">
        <label htmlFor="cancel-name">
          <span>Dein Name</span>
        </label>
        <input
          id="cancel-name"
          type="text"
          maxLength={200}
          autoComplete="name"
          {...register('name')}
        />
        {errors.name ? (
          <p className="field-error">{errors.name.message}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="cancel-email">
          <span>Deine E-Mail-Adresse</span>
        </label>
        <input
          id="cancel-email"
          type="email"
          autoComplete="email"
          {...register('email')}
        />
        {errors.email ? (
          <p className="field-error">{errors.email.message}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="cancel-reference">
          <span>Vertrags- oder Rechnungsbezug (optional)</span>
        </label>
        <input
          id="cancel-reference"
          type="text"
          maxLength={200}
          {...register('reference')}
        />
        <p className="form-hint">
          Falls vorhanden, hilft uns z.B. eine Rechnungs- oder Vertragsnummer
          bei der Zuordnung.
        </p>
        {errors.reference ? (
          <p className="field-error">{errors.reference.message}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="cancel-reason">
          <span>Grund (optional)</span>
        </label>
        <textarea id="cancel-reason" maxLength={2000} {...register('reason')} />
        <p className="form-hint">
          Freiwillig – dein Feedback hilft uns, Locari zu verbessern.
        </p>
        {errors.reason ? (
          <p className="field-error">{errors.reason.message}</p>
        ) : null}
      </div>

      <div className="submit-row">
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={pending}
        >
          {pending
            ? 'Senden…'
            : confirming
              ? 'jetzt kündigen'
              : 'Verträge hier kündigen'}
        </button>
      </div>

      {confirming ? (
        <p className="form-note" role="status" aria-live="polite">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
          <span>
            Bitte bestätige deine Kündigung mit „jetzt kündigen“. Wir bestätigen
            den Eingang anschließend per E-Mail.
          </span>
        </p>
      ) : (
        <p className="form-note">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>
            Wir bestätigen den Eingang deiner Kündigung per E-Mail an die
            angegebene Adresse.
          </span>
        </p>
      )}

      {errors.root ? (
        <p className="field-error" role="alert">
          {errors.root.message}
        </p>
      ) : null}
    </form>
  );
}
